/**
 * Presentation helpers shared by every surface.
 *
 * Two things here are worth more than they look.
 *
 * **Money** is formatted from integer minor units and an explicit currency, and
 * the minor-unit exponent comes from the runtime's own currency data rather than
 * an assumed two. Getting this wrong is not cosmetic when the number is what
 * somebody is charged.
 *
 * **Time** is always formatted in a named zone. An event's times belong to the
 * venue's wall clock; a browser in another country rendering them in its own
 * would tell an attendee the wrong hour to arrive.
 */

export interface Money {
  readonly minor: number;
  readonly currency: string;
}

/** Minor units per major unit, from the runtime's currency data. */
function minorUnitFactor(currency: string, locale: string): number {
  try {
    const digits =
      new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    return 10 ** digits;
  } catch {
    return 100;
  }
}

export function formatMoney(money: Money, locale = 'en-NG'): string {
  const factor = minorUnitFactor(money.currency, locale);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: money.currency,
      currencyDisplay: 'narrowSymbol',
    }).format(money.minor / factor);
  } catch {
    // An unknown currency code is still worth rendering legibly.
    return `${money.currency} ${(money.minor / factor).toFixed(2)}`;
  }
}

/** True when the amount is zero, so a surface can say "Free" rather than "₦0.00". */
export function isFree(money: Money): boolean {
  return money.minor === 0;
}

export function formatMoneyOrFree(money: Money, locale = 'en-NG'): string {
  return isFree(money) ? 'Free' : formatMoney(money, locale);
}

export interface ZonedTimeOptions {
  readonly locale?: string;
  readonly withYear?: boolean;
  readonly withZoneName?: boolean;
}

/**
 * Formats an instant in a named zone.
 *
 * `timeZone` is required, never defaulted to the browser's. A Lagos event read
 * on a phone in London must say the Lagos time, because that is when the doors
 * open.
 */
export function formatInZone(
  iso: string,
  timeZone: string,
  options: ZonedTimeOptions = {},
): string {
  const locale = options.locale ?? 'en-GB';
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      ...(options.withYear === true ? { year: 'numeric' } : {}),
      hour: '2-digit',
      minute: '2-digit',
      ...(options.withZoneName === true ? { timeZoneName: 'short' } : {}),
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatDateInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function formatTimeInZone(iso: string, timeZone: string, locale = 'en-GB'): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** A compact range, collapsing the date when both ends fall on the same day. */
export function formatRangeInZone(
  startIso: string,
  endIso: string,
  timeZone: string,
  locale = 'en-GB',
): string {
  const sameDay =
    formatDateInZone(startIso, timeZone, locale) === formatDateInZone(endIso, timeZone, locale);
  if (sameDay) {
    return `${formatInZone(startIso, timeZone, { locale })} – ${formatTimeInZone(endIso, timeZone, locale)}`;
  }
  return `${formatInZone(startIso, timeZone, { locale })} – ${formatInZone(endIso, timeZone, { locale })}`;
}

/**
 * Time remaining until an instant, in words a person waiting can act on.
 *
 * Rendered from the *server's* expiry, never from a client-side calculation of
 * when a hold "should" have lapsed. A browser whose clock is wrong must not be
 * able to decide that somebody's tickets are gone.
 */
export function formatCountdown(untilIso: string, nowMs: number): string {
  const remaining = new Date(untilIso).getTime() - nowMs;
  if (Number.isNaN(remaining) || remaining <= 0) return 'expired';

  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${String(hours)}h ${String(minutes % 60)}m`;
  }
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

/** Whether an expiry has passed, as far as this client can tell. */
export function hasLapsed(untilIso: string, nowMs: number): boolean {
  const at = new Date(untilIso).getTime();
  return Number.isNaN(at) || at <= nowMs;
}
